import ko from 'knockout';
import axios from "axios";
import { Dropdown, Offcanvas } from "bootstrap";
import { initLoadMore } from './catalog/load-more';

interface SelectedFilter {
    FacetName: string;
    FacetNameSlug: string;
    FacetValue: string;
    FacetValueSlug: string;
}

interface RangeFilter {
    facetSlug: string;
    min: number;
    max: number;
}

interface FacetElement {
    element: HTMLElement;
    width: number;
    priority: number;
    slug: string;
}

function SortByToFriendly(sortOn: string): string {
    switch (sortOn) {
        case "asc":
            return "Price: Low to High";
        case "desc":
            return "Price: High to Low";
        case "best":
            return "Best Selling";
        default:
            return "Price: Low to High";
    }
}

class HorizontalFacetsOverflow {
    private container: HTMLElement | null;
    private primaryContainer: HTMLElement | null;
    private moreContainer: HTMLElement | null;
    private overflowAccordion: HTMLElement | null;
    private facetElements: FacetElement[] = [];
    private resizeObserver: ResizeObserver | null = null;
    private readonly MORE_BUTTON_WIDTH = 140;
    private readonly SORT_WIDTH = 180;
    private readonly GAP_WIDTH = 8;

    constructor() {
        this.container = document.getElementById('facetsHorizontalBar');
        this.primaryContainer = document.getElementById('facetsPrimary');
        this.moreContainer = document.getElementById('facetsMoreContainer');
        this.overflowAccordion = document.getElementById('facetsOverflowAccordion');

        if (!this.container || !this.primaryContainer) return;

        this.initialize();
    }

    private initialize(): void {
        // Collect all facet dropdown elements with their widths
        const facetDropdowns = this.primaryContainer!.querySelectorAll('.facet-dropdown');
        facetDropdowns.forEach((el: Element) => {
            const htmlEl = el as HTMLElement;
            this.facetElements.push({
                element: htmlEl,
                width: htmlEl.offsetWidth,
                priority: parseInt(htmlEl.dataset.facetPriority || '100'),
                slug: htmlEl.dataset.facetSlug || ''
            });
        });

        // Sort by priority (display order)
        this.facetElements.sort((a, b) => a.priority - b.priority);

        // Set up resize observer for responsive behavior
        this.resizeObserver = new ResizeObserver(() => {
            this.calculateOverflow();
        });
        this.resizeObserver.observe(this.container!);

        // Initial calculation after a brief delay for layout
        setTimeout(() => this.calculateOverflow(), 100);
    }

    private calculateOverflow(): void {
        if (!this.container || !this.primaryContainer || !this.moreContainer) return;

        const containerWidth = this.container.offsetWidth;
        const availableWidth = containerWidth - this.SORT_WIDTH - this.MORE_BUTTON_WIDTH - (this.GAP_WIDTH * 3);

        let usedWidth = 0;
        const visibleFacets: FacetElement[] = [];
        const overflowFacets: FacetElement[] = [];

        for (const facet of this.facetElements) {
            if (usedWidth + facet.width + this.GAP_WIDTH <= availableWidth) {
                visibleFacets.push(facet);
                usedWidth += facet.width + this.GAP_WIDTH;
            } else {
                overflowFacets.push(facet);
            }
        }

        // Update visibility
        this.facetElements.forEach(f => {
            f.element.classList.remove('d-none', 'in-overflow');
        });

        overflowFacets.forEach(f => {
            f.element.classList.add('d-none', 'in-overflow');
        });

        // Show/hide "More Filters" button
        if (overflowFacets.length > 0) {
            this.moreContainer.classList.remove('d-none');
            this.buildOverflowAccordion(overflowFacets);
            this.updateMoreFiltersBadge(overflowFacets);
        } else {
            this.moreContainer.classList.add('d-none');
        }
    }

    private buildOverflowAccordion(overflowFacets: FacetElement[]): void {
        if (!this.overflowAccordion) return;

        // Clear existing content
        this.overflowAccordion.innerHTML = '';

        overflowFacets.forEach(facet => {
            // Find corresponding mobile accordion item and clone it
            const mobileAccordionItem = document.querySelector(
                `#facetsMobileAccordion .accordion-item[data-facet-slug="${facet.slug}"]`
            );

            if (mobileAccordionItem) {
                const clone = mobileAccordionItem.cloneNode(true) as HTMLElement;

                // Update IDs to avoid conflicts
                clone.querySelectorAll('[id]').forEach((el: Element) => {
                    const htmlEl = el as HTMLElement;
                    htmlEl.id = 'overflow-' + htmlEl.id;
                });
                clone.querySelectorAll('[data-bs-target]').forEach((el: Element) => {
                    const htmlEl = el as HTMLElement;
                    const target = htmlEl.dataset.bsTarget;
                    if (target) htmlEl.dataset.bsTarget = '#overflow-' + target.substring(1);
                });
                clone.querySelectorAll('[aria-controls]').forEach((el: Element) => {
                    const controls = el.getAttribute('aria-controls');
                    if (controls) el.setAttribute('aria-controls', 'overflow-' + controls);
                });

                this.overflowAccordion!.appendChild(clone);
            }
        });
    }

    private updateMoreFiltersBadge(overflowFacets: FacetElement[]): void {
        // Count selected values in overflow facets
        let selectedCount = 0;
        overflowFacets.forEach(facet => {
            const container = facet.element;
            selectedCount += container.querySelectorAll(
                '.facet-checkbox-v3:checked, .facet-radio-v3:checked, .facet-toggle-v3:checked, .facet-color-swatch.selected'
            ).length;
        });

        const badge = document.getElementById('moreFiltersBadge');
        if (badge) {
            if (selectedCount > 0) {
                badge.textContent = selectedCount.toString();
                badge.classList.remove('d-none');
            } else {
                badge.classList.add('d-none');
            }
        }
    }

    public recalculate(): void {
        // Re-measure widths (useful after font loading)
        this.facetElements.forEach(f => {
            f.width = f.element.offsetWidth;
        });
        this.calculateOverflow();
    }

    public destroy(): void {
        this.resizeObserver?.disconnect();
    }
}

var FiltersHorizontalV3 = {
    SortByToFriendly: function (sortOn: string): string {
        return SortByToFriendly(sortOn);
    },

    init: async function (data: KnockoutObservableArray<SelectedFilter>, sortByData: KnockoutObservable<string>) {
        const rangeFilters: Map<string, RangeFilter> = new Map();
        let overflowManager: HorizontalFacetsOverflow | null = null;

        // Initialize overflow calculation for desktop
        if (window.innerWidth >= 992) {
            overflowManager = new HorizontalFacetsOverflow();
        }

        // JavaScript-based sticky behavior (CSS sticky breaks with overflow-x:hidden on body)
        const horizontalBar = document.getElementById('facetsHorizontalBar');
        const stickyHeader = document.querySelector('.sticky-header') as HTMLElement;
        if (horizontalBar) {
            const placeholder = document.createElement('div');
            placeholder.style.display = 'none';
            horizontalBar.parentNode?.insertBefore(placeholder, horizontalBar);

            let barOriginalTop = horizontalBar.getBoundingClientRect().top + window.scrollY;
            let isFixed = false;

            const updateStickyState = () => {
                const stickyHeaderVisible = stickyHeader && stickyHeader.classList.contains('visible');
                const stickyHeaderHeight = stickyHeaderVisible ? stickyHeader.offsetHeight : 0;
                const scrollThreshold = barOriginalTop - stickyHeaderHeight;

                if (window.scrollY >= scrollThreshold) {
                    if (!isFixed) {
                        // Store original dimensions
                        const rect = horizontalBar.getBoundingClientRect();
                        placeholder.style.height = rect.height + 'px';
                        placeholder.style.display = 'block';

                        horizontalBar.style.position = 'fixed';
                        horizontalBar.style.top = stickyHeaderHeight + 'px';
                        horizontalBar.style.left = '0';
                        horizontalBar.style.right = '0';
                        horizontalBar.style.zIndex = '1030';
                        horizontalBar.classList.add('scrolled');
                        isFixed = true;
                    } else {
                        // Update top position if sticky header visibility changed
                        horizontalBar.style.top = stickyHeaderHeight + 'px';
                    }
                } else {
                    if (isFixed) {
                        placeholder.style.display = 'none';
                        horizontalBar.style.position = '';
                        horizontalBar.style.top = '';
                        horizontalBar.style.left = '';
                        horizontalBar.style.right = '';
                        horizontalBar.style.zIndex = '';
                        horizontalBar.classList.remove('scrolled');
                        isFixed = false;
                    }
                }
            };

            // Recalculate original position on resize
            window.addEventListener('resize', () => {
                if (!isFixed) {
                    barOriginalTop = horizontalBar.getBoundingClientRect().top + window.scrollY;
                }
            });

            window.addEventListener('scroll', updateStickyState, { passive: true });

            // Also observe sticky header class changes
            if (stickyHeader) {
                const observer = new MutationObserver(updateStickyState);
                observer.observe(stickyHeader, { attributes: true, attributeFilter: ['class'] });
            }
        }

        // Add body class when facets offcanvas opens (for backdrop z-index scoping)
        const facetsOffcanvas = document.getElementById('facetsMobileOffcanvas');
        if (facetsOffcanvas) {
            facetsOffcanvas.addEventListener('show.bs.offcanvas', () => {
                document.body.classList.add('facets-offcanvas-open');
            });
            facetsOffcanvas.addEventListener('hidden.bs.offcanvas', () => {
                document.body.classList.remove('facets-offcanvas-open');
            });
        }

        // Mobile filter bar - JS-based sticky (CSS sticky breaks with overflow-x:hidden on body)
        const mobileFilterBar = document.querySelector('.facets-mobile-v3') as HTMLElement;
        if (mobileFilterBar && window.innerWidth < 992) {
            const mobilePlaceholder = document.createElement('div');
            mobilePlaceholder.style.display = 'none';
            mobileFilterBar.parentNode?.insertBefore(mobilePlaceholder, mobileFilterBar);

            let mobileBarOriginalTop = mobileFilterBar.getBoundingClientRect().top + window.scrollY;
            let mobileIsFixed = false;

            const updateMobileStickyState = () => {
                if (window.scrollY >= mobileBarOriginalTop) {
                    if (!mobileIsFixed) {
                        const rect = mobileFilterBar.getBoundingClientRect();
                        mobilePlaceholder.style.height = rect.height + 'px';
                        mobilePlaceholder.style.display = 'block';

                        mobileFilterBar.style.position = 'fixed';
                        mobileFilterBar.style.top = '0';
                        mobileFilterBar.style.left = '0';
                        mobileFilterBar.style.right = '0';
                        mobileFilterBar.style.zIndex = '1020';
                        mobileFilterBar.classList.add('scrolled');
                        mobileIsFixed = true;
                    }
                } else {
                    if (mobileIsFixed) {
                        mobilePlaceholder.style.display = 'none';
                        mobileFilterBar.style.position = '';
                        mobileFilterBar.style.top = '';
                        mobileFilterBar.style.left = '';
                        mobileFilterBar.style.right = '';
                        mobileFilterBar.style.zIndex = '';
                        mobileFilterBar.classList.remove('scrolled');
                        mobileIsFixed = false;
                    }
                }
            };

            window.addEventListener('resize', () => {
                if (!mobileIsFixed) {
                    mobileBarOriginalTop = mobileFilterBar.getBoundingClientRect().top + window.scrollY;
                }
            });

            window.addEventListener('scroll', updateMobileStickyState, { passive: true });
        }

        // Sort by dropdown handlers
        $(".sortBy").on('click', function () {
            var sortOn = $(this).attr('data-sortOn');
            sortByData(sortOn);
            $(".sortByText").html(SortByToFriendly(sortByData()));
            updateFromFilters();

            // Close dropdown
            const dropdown = $(this).closest('.dropdown').find('.dropdown-toggle')[0];
            if (dropdown) {
                const dd = Dropdown.getInstance(dropdown);
                dd?.hide();
            }
            return false;
        });

        // Clear all filters
        $("#btn-clear-filters, #btn-clear-all-desktop, #btn-clear-filters-mobile").on('click', function (e) {
            e.preventDefault();
            clearAllFilters();
        });

        function clearAllFilters() {
            data.removeAll();
            rangeFilters.clear();
            $(".facet-checkbox-v3, .facet-toggle-v3").prop('checked', false);
            $(".facet-radio-v3").prop('checked', false);
            $(".facet-color-swatch").removeClass('selected');

            // Reset range sliders
            $(".facet-range-slider").each(function () {
                const container = $(this);
                const min = parseFloat(container.data('min'));
                const max = parseFloat(container.data('max'));
                container.find('.range-min-input, .range-slider-min').val(min);
                container.find('.range-max-input, .range-slider-max').val(max);
            });

            updateAllBadges();
            updateFromFilters();
        }

        // Remove selected filter tag
        $(document).on('click', ".selected-filter-tag", function () {
            let facetNameSlug = $(this).attr('data-facetNameSlug');
            let facetValueSlug = $(this).attr('data-facetValueSlug');

            // Uncheck the corresponding checkbox/radio/toggle
            $(`#facetv3_${facetNameSlug}_${facetValueSlug}`).prop('checked', false);
            $(`#facetv3_radio_${facetNameSlug}_${facetValueSlug}`).prop('checked', false);
            $(`#facetv3_toggle_${facetNameSlug}_${facetValueSlug}`).prop('checked', false);

            // Remove swatch selection
            $(`.facet-color-swatch[data-facet-slug="${facetNameSlug}"][data-facet-value-slug="${facetValueSlug}"]`).removeClass('selected');

            // Remove from selected filter
            data.remove(function (item) {
                return (ko.unwrap(item.FacetNameSlug) == facetNameSlug && ko.unwrap(item.FacetValueSlug) == facetValueSlug);
            });

            // Remove range filter if applicable
            rangeFilters.delete(facetNameSlug!);

            updateFacetButtonBadge(facetNameSlug!);
            updateFromFilters();
        });

        // Prevent all default behavior on facet links
        $(document).on('mousedown click', '.facet-value-link', function (e) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
        });

        // Handle the actual toggle on mouseup (after all defaults prevented)
        $(document).on('mouseup', '.facet-value-link', function (e) {
            const checkbox = $(this).find('.facet-checkbox-v3').first();
            if (checkbox.length) {
                const newState = !checkbox.prop('checked');
                checkbox.prop('checked', newState);
                handleCheckboxChange(checkbox[0] as HTMLInputElement);
            }
        });

        // Note: Checkbox changes are handled directly in click handlers above
        // No separate change handler needed

        // Radio handlers
        $(document).on('change', '.facet-radio-v3', function (e) {
            const radio = e.currentTarget as HTMLInputElement;
            handleRadioChange(radio);
        });

        // Toggle handlers
        $(document).on('change', '.facet-toggle-v3', function (e) {
            const toggle = e.currentTarget as HTMLInputElement;
            handleCheckboxChange(toggle);
        });

        // Color swatch handlers - prevent default on all events
        $(document).on('mousedown click touchstart', '.facet-color-swatch', function (e) {
            e.preventDefault();
            e.stopPropagation();
        });

        // Handle color swatch toggle on mouseup or touchend
        let swatchHandled = false;
        $(document).on('mouseup touchend', '.facet-color-swatch', function (e) {
            e.preventDefault();

            // Prevent double-handling from both mouse and touch events
            if (swatchHandled) return;
            swatchHandled = true;
            setTimeout(() => { swatchHandled = false; }, 100);

            const swatch = $(e.currentTarget);
            const facetSlug = swatch.data('facet-slug');
            const valueSlug = swatch.data('facet-value-slug');
            const isSelected = swatch.hasClass('selected');

            if (isSelected) {
                swatch.removeClass('selected');
                data.remove(function (item) {
                    return (ko.unwrap(item.FacetNameSlug) == facetSlug && ko.unwrap(item.FacetValueSlug) == valueSlug);
                });
            } else {
                swatch.addClass('selected');
                data.push({
                    FacetName: swatch.data('facet-name') || swatch.attr('title') || '',
                    FacetNameSlug: facetSlug,
                    FacetValue: swatch.attr('title') || '',
                    FacetValueSlug: valueSlug
                });
            }
            updateFacetButtonBadge(facetSlug);
            updateFromFilters();
        });

        // Range slider handlers
        $(document).on('input', '.range-slider-min, .range-slider-max', function () {
            const container = $(this).closest('.facet-range-slider');
            syncRangeInputs(container, 'slider');
        });

        $(document).on('change', '.range-min-input, .range-max-input', function () {
            const container = $(this).closest('.facet-range-slider');
            syncRangeInputs(container, 'input');
        });

        // Apply range filter button
        $(document).on('click', '.apply-range-filter', function () {
            const container = $(this).closest('.facet-range-slider');
            const facetSlug = container.data('facet-slug');
            const min = parseFloat(container.find('.range-min-input').val() as string);
            const max = parseFloat(container.find('.range-max-input').val() as string);
            const dataMin = parseFloat(container.data('min'));
            const dataMax = parseFloat(container.data('max'));

            // Only add filter if values differ from defaults
            if (min !== dataMin || max !== dataMax) {
                rangeFilters.set(facetSlug, { facetSlug, min, max });
            } else {
                rangeFilters.delete(facetSlug);
            }

            updateFacetButtonBadge(facetSlug);
            updateFromFilters();
        });

        // Search within facet
        $(document).on('input', '.facet-search-input', function () {
            const input = $(this);
            const searchTerm = (input.val() as string).toLowerCase();
            const container = input.closest('.accordion-body, .facet-dropdown-body');

            container.find('.facet-value-item').each(function () {
                const item = $(this);
                const searchable = (item.data('searchable') || '').toLowerCase();
                const label = item.find('label').text().toLowerCase();
                if (searchable.includes(searchTerm) || label.includes(searchTerm)) {
                    item.closest('a, .facet-value-link').show();
                } else {
                    item.closest('a, .facet-value-link').hide();
                }
            });
        });

        function handleCheckboxChange(checkbox: HTMLInputElement) {
            const checked = checkbox.checked;
            const facetSlug = checkbox.dataset.facetSlug || '';
            const valueSlug = checkbox.dataset.facetValueSlug || '';
            const facetName = checkbox.dataset.facetName || '';
            const facetValue = checkbox.dataset.facetValue || '';

            if (checked) {
                data.push({
                    FacetName: facetName,
                    FacetNameSlug: facetSlug,
                    FacetValue: facetValue,
                    FacetValueSlug: valueSlug
                });
            } else {
                data.remove(function (item) {
                    return (ko.unwrap(item.FacetNameSlug) == facetSlug && ko.unwrap(item.FacetValueSlug) == valueSlug);
                });
            }

            updateFacetButtonBadge(facetSlug);
            updateFromFilters();
        }

        function handleRadioChange(radio: HTMLInputElement) {
            const facetSlug = radio.dataset.facetSlug || '';
            const valueSlug = radio.dataset.facetValueSlug || '';
            const facetName = radio.dataset.facetName || '';
            const facetValue = radio.dataset.facetValue || '';

            // Remove all existing selections for this facet
            data.remove(function (item) {
                return ko.unwrap(item.FacetNameSlug) == facetSlug;
            });

            // Add the new selection
            data.push({
                FacetName: facetName,
                FacetNameSlug: facetSlug,
                FacetValue: facetValue,
                FacetValueSlug: valueSlug
            });

            updateFacetButtonBadge(facetSlug);
            updateFromFilters();
        }

        function syncRangeInputs(container: JQuery, source: 'slider' | 'input') {
            const dataMin = parseFloat(container.data('min'));
            const dataMax = parseFloat(container.data('max'));

            let minVal: number, maxVal: number;

            if (source === 'slider') {
                minVal = parseFloat(container.find('.range-slider-min').val() as string);
                maxVal = parseFloat(container.find('.range-slider-max').val() as string);

                // Ensure min doesn't exceed max
                if (minVal > maxVal) {
                    minVal = maxVal;
                    container.find('.range-slider-min').val(minVal);
                }

                container.find('.range-min-input').val(minVal);
                container.find('.range-max-input').val(maxVal);
            } else {
                minVal = parseFloat(container.find('.range-min-input').val() as string);
                maxVal = parseFloat(container.find('.range-max-input').val() as string);

                // Clamp values
                minVal = Math.max(dataMin, Math.min(minVal, maxVal));
                maxVal = Math.min(dataMax, Math.max(minVal, maxVal));

                container.find('.range-min-input').val(minVal);
                container.find('.range-max-input').val(maxVal);
                container.find('.range-slider-min').val(minVal);
                container.find('.range-slider-max').val(maxVal);
            }
        }

        function updateFacetButtonBadge(facetSlug: string) {
            // Update desktop dropdown button badge
            const container = $(`.facet-dropdown[data-facet-slug="${facetSlug}"]`);
            const button = container.find('.btn-facet-dropdown');
            let badge = button.find('.badge');

            const selectedCount = container.find(
                '.facet-checkbox-v3:checked, .facet-radio-v3:checked, .facet-toggle-v3:checked, .facet-color-swatch.selected'
            ).length;

            if (selectedCount > 0) {
                button.addClass('has-selections');
                if (badge.length) {
                    badge.text(selectedCount);
                } else {
                    button.append(`<span class="badge bg-primary ms-1">${selectedCount}</span>`);
                }
            } else {
                button.removeClass('has-selections');
                badge.remove();
            }

            // Update mobile accordion header badge
            const mobileAccordion = $(`#facetsMobileAccordion .accordion-item[data-facet-slug="${facetSlug}"]`);
            const mobileButton = mobileAccordion.find('.accordion-button');
            let mobileBadgeEl = mobileButton.find('.badge');

            // Count from mobile accordion
            const mobileSelectedCount = mobileAccordion.find(
                '.facet-checkbox-v3:checked, .facet-radio-v3:checked, .facet-toggle-v3:checked, .facet-color-swatch.selected'
            ).length;

            if (mobileSelectedCount > 0) {
                if (mobileBadgeEl.length) {
                    mobileBadgeEl.text(mobileSelectedCount);
                } else {
                    mobileButton.append(`<span class="badge bg-primary ms-2">${mobileSelectedCount}</span>`);
                }
            } else {
                mobileBadgeEl.remove();
            }

            // Update mobile total badge
            updateMobileBadge();
        }

        function updateAllBadges() {
            $('.facet-dropdown').each(function () {
                const slug = $(this).data('facet-slug');
                if (slug) {
                    updateFacetButtonBadge(slug);
                }
            });
            updateMobileBadge();
        }

        function updateMobileBadge() {
            const totalCount = data().length;
            const mobileBadge = $('#mobileFiltersBadge');
            if (totalCount > 0) {
                mobileBadge.text(totalCount).removeClass('d-none');
            } else {
                mobileBadge.addClass('d-none');
            }
        }

        // Subscribe to data changes to update badges
        data.subscribe(() => {
            updateMobileBadge();
        });

        async function updateFromFilters() {
            // Build facet filter string from checkboxes/radios/toggles
            let facets: string[] = [];

            // Collect checkbox, radio, and toggle selections
            $(".facet-checkbox-v3:checked, .facet-radio-v3:checked, .facet-toggle-v3:checked").each(function (index, elem: HTMLInputElement) {
                facets.push('--' + elem.dataset.facetSlug + '_' + elem.dataset.facetValueSlug);
            });

            // Collect color swatch selections
            $(".facet-color-swatch.selected").each(function () {
                const swatch = $(this);
                facets.push('--' + swatch.data('facet-slug') + '_' + swatch.data('facet-value-slug'));
            });

            // Collect range filter selections
            rangeFilters.forEach((range) => {
                facets.push('--' + range.facetSlug + '_' + range.min + '-' + range.max);
            });

            facets.sort();

            var form = $("#filterform");
            var actionUrl = form.attr("action");
            var baseActionUrl = form.attr('data-baseactionurl');
            var query = form.attr('data-baseactionquery');

            var postData = {
                facetFilterValues: facets,
                sortBy: sortByData()
            };

            // Show loading state
            $("#catalogContainer").css({ 'opacity': 0.5 });
            $(".facet-dropdown-panel, .facets-offcanvas-v3 .accordion-body").css({ 'opacity': 0.7 });

            if (query != null)
                actionUrl += '?query=' + query;

            try {
                var result = await axios.post(actionUrl!, postData);

                var pushUrl = facets.join("");
                var url: URL;
                if (pushUrl != '') {
                    url = new URL(baseActionUrl + '/' + pushUrl + '/' + sortByData(), window.location.origin);
                } else {
                    url = new URL(baseActionUrl + '/' + sortByData(), window.location.origin);
                }

                if (query != null)
                    url.searchParams.set("query", query);

                history.pushState({}, "", url);

                // Replace catalog container content
                $("#catalogContainer").html(result.data);

                // Re-initialize load more button after HTML replacement
                initLoadMore();

                // Remove loading state
                $("#catalogContainer").css({ 'opacity': 1 });
                $(".facet-dropdown-panel, .facets-offcanvas-v3 .accordion-body").css({ 'opacity': 1 });
            } catch (err) {
                console.error('Error updating filters:', err);
                $("#catalogContainer").css({ 'opacity': 1 });
                $(".facet-dropdown-panel, .facets-offcanvas-v3 .accordion-body").css({ 'opacity': 1 });
            }
        }
    }
};

export { FiltersHorizontalV3 as default };
